import { FC, useMemo, useCallback, ChangeEvent, ReactNode } from 'react'
import { FutureLayerUID, FutureLayer, FutureLayerType, FutureLayerStatus } from 'components/features/future/types'
import { FutureInputDecorationLabel } from 'components/features/future/FutureInputDecoration/FutureInputDecorationLabel'
import { FutureCheckbox } from 'components/features/future/FutureCheckbox/FutureCheckbox'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { deleteFutureLayerActionCreator, completeFutureLayerActionCreator, partialCompleteFutureLayerActionCreator } from 'components/features/future/actions'
import styles from 'components/features/future/LayerStatus/LayerStatus.styl'
import { useFormContext } from 'react-hook-form'
import { PanePlate } from 'components/atoms/PanePlate/PanePlate'
import { PastCard } from 'components/features/future/FutureLayerPast/PastCard/PastCard'
import { PastCardValues } from 'components/features/future/FutureLayerPast/types'
import { CompleteCard } from 'components/features/future/FutureLayerPast/CompleteCard/CompleteCard'

type LayerStatusProps = {
  pastContent?: ReactNode
  completeContent?: ReactNode
  skipPartialState?: boolean
  uid: FutureLayerUID | null
  type: FutureLayerType

}
export const LayerStatus: FC<LayerStatusProps> = (props) => {
  const { children, uid, pastContent, completeContent, skipPartialState, type } = props

  const { setValue, watch, register } = useFormContext<Pick<FutureLayer, 'status'>>()
  const status = watch('status') as FutureLayer['status'] | null

  const handleEnabledChange = useCallback((_event: ChangeEvent<HTMLInputElement>) => {
    if (status === 'enabled') {
      setValue('status', 'disabled', { shouldDirty: true, shouldValidate: true })
      return
    }

    setValue('status', 'enabled', { shouldDirty: true, shouldValidate: true })
  }, [status, setValue])

  const includeSwitchChild = useMemo(() => {
    if (!uid) {
      return null
    }

    const isEnabled = !status || status === 'enabled'

    return (
      <label className={ styles.includeSwitch }>
        <FutureInputDecorationLabel>
          Include in forecast model?
        </FutureInputDecorationLabel>
        <div className={ styles.isEnabled }>
          <FutureCheckbox checked={ isEnabled } onChange={ handleEnabledChange } />
        </div>
      </label>
    )
  }, [uid, status, handleEnabledChange])

  const dispatch = useStoreDispatch()

  const handleRemoveClick = useMemo(() => () => {
    if (!uid) {
      return
    }

    dispatch(deleteFutureLayerActionCreator.request({ uid }))
  }, [uid, dispatch])

  const removeButtonChild = useMemo(() => {
    if (!uid) {
      return
    }

    return (
      <div className={ styles.buttonWrapper }>
        <button type='button' className={ styles.removeButton } onClick={ handleRemoveClick }>
          Remove
        </button>
      </div>
    )
  }, [uid, handleRemoveClick])

  const DEBUG = false
  const setStatusButtonsChild = useMemo(() => {
    if (!DEBUG) {
      return null
    }

    const handler = (nextStatus: FutureLayerStatus) => {
      setValue('status', nextStatus, { shouldDirty: true, shouldValidate: true })
    }

    return (
      <>
        <br />
        <br />
        <br />
        <button type='button' className={ styles.debugButton } onClick={ () => handler('enabled') }>
          to enabled
        </button>
        <button type='button' className={ styles.debugButton } onClick={ () => handler('disabled') }>
          to disabled
        </button>
        <button type='button' className={ styles.debugButton } onClick={ () => handler('past') }>
          to past
        </button>
        <button type='button' className={ styles.debugButton } onClick={ () => handler('partially_completed') }>
          to partially_completed
        </button>
        <button type='button' className={ styles.debugButton } onClick={ () => handler('completed') }>
          to completed
        </button>
      </>
    )
  }, [setValue, DEBUG])

  const handleCompleteActiveLayer = useCallback(() => {
    if (!uid) {
      return
    }

    if (skipPartialState) {
      dispatch(completeFutureLayerActionCreator.request({ uid }))
      return
    }

    dispatch(partialCompleteFutureLayerActionCreator.request({ uid }))
  }, [dispatch, uid, skipPartialState])

  const completeButtonChild = useMemo(() => {
    if (!uid || !status || status === 'completed' || status === 'past' || status === 'partially_completed') {
      return
    }

    return (
      <div className={ styles.buttonWrapper }>
        <button type='button' className={ styles.completeButton } onClick={ handleCompleteActiveLayer }>
          Mark As Completed
        </button>
      </div>
    )
  }, [uid, handleCompleteActiveLayer, status])

  const handleCompletePastLayer = useCallback((values: Partial<PastCardValues>) => {
    if (!uid) {
      return
    }

    dispatch(completeFutureLayerActionCreator.request({
      uid,
      ...values,
    }))
  }, [dispatch, uid])

  const handleUndoComplete = useCallback(() => {
    setValue('status', 'enabled', { shouldDirty: true, shouldValidate: true })
  }, [setValue])

  const statusCardContentChild = useMemo(() => {
    if (status === 'past') {
      return (
        <PastCard action='MARK AS COMPLETED' onAction={ handleCompletePastLayer }>
          <h1>
            The date for this { type } has passed
          </h1>
          Edit it below or mark it as completed.
          { pastContent }
        </PastCard>
      )
    }

    if (status === 'partially_completed') {
      return (
        <PastCard action='UPDATE' onAction={ handleCompletePastLayer } actionTheme='lightBlue'>
          { type === 'goal' && (
            <h1>Congratulations!</h1>
          )}
          { type === 'event' && (
            <h1>Event Completed</h1>
          )}
          { pastContent }
        </PastCard>
      )
    }

    if (status === 'completed') {
      return (
        <CompleteCard onAction={ handleUndoComplete } type={ type }>
          { completeContent }
        </CompleteCard>
      )
    }

    return null
  }, [status, completeContent, pastContent, handleCompletePastLayer, handleUndoComplete, type])

  const statusCardChild = useMemo(() => {
    if (!statusCardContentChild) {
      return null
    }

    return (
      <PanePlate className={ styles.completeCard }>
        { statusCardContentChild }
      </PanePlate>
    )
  }, [statusCardContentChild])


  const faderChild = useMemo(() => {
    if (status !== 'completed' && status !== 'partially_completed') {
      return null
    }

    return (
      <div className={ styles.fader } />
    )
  }, [status])

  return (
    <>
      <input type='hidden' { ...register('status') } />
      { statusCardChild }
      <div className={ styles.content }>
        { children }
        { includeSwitchChild }
        { faderChild }
      </div>
      { removeButtonChild }
      { completeButtonChild }
      { setStatusButtonsChild }
    </>
  )
}
